// 2) Third-party libraries
import { get, isEqual, keyBy, merge, uniqWith, values } from "lodash-es";
import { createAction, createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

// 3) Local imports
// 3.1 Constants
import { CUSTOMER_ASSISTANCE_PAGE, DETAILS_TAB } from "../../config/tableAndPageConstants";
import { conversationTableFilterOptions } from "../../constants/common";
import { ticketsSortByOptions, ticketsStatusFilterOptions } from "../../constants/customerAssistanceSortAndFilters";
import { invoicesStateFilterOptions, commonInvoiceListInvoicesSortOptions } from "../../constants/invoicesSortAndFilters";

// 3.3 Project-specific modules � components/types
import {
    GetTicketDataInvoicesRequestPayload,
    GetTicketDataTasksRequestPayload,
    GetTicketDiscussionRequestPayload,
    GetTicketsRequestPayload,
    Ticket,
    TicketsState,
    TicketAssignUserRequestPayload,
    TicketAddCommentRequestPayload,
    TicketResourceReplyRequestPayload,
    GetTicketInfoForChatValidationPayload,
} from "./types";
import {
    Conversation,
    ConversationLine,
    GetConversationRequestPayload,
    PageData,
} from "../common/types";
import { Invoice } from "../invoices/types";
import { Task } from "../tasks/types";

// 3.4 Utility functions / types
import { DynamicObject } from "../../utils/commonInterfaces";

// 3.5 Redux actions / store
import type { RootState } from "../storeTypes";

const actionPrefix = 'ticket';

// Type-safe initialState!
export const initialState: TicketsState = {
    loading: false,
    saveTicketDataLoading: false,
    errorMessages: [],
    data: [],
    pageData: {
        pageSize: CUSTOMER_ASSISTANCE_PAGE.pageSize,
        currentPage: 0,
        hasNextPage: false,
    },
    filters: {
        DisplayName: '',
        CustomerCode: '',
        CreatedDateMin: '',
        CreatedDateMax: '',
        ClosedDateMin: '',
        ClosedDateMax: '',
        LastChatDateMin: '',
        LastChatDateMax: '',
        Status: -1,
        TicketOptionReasons: [],
    },
    ticketStatus: get(ticketsStatusFilterOptions, `${0}.value`),
    ticketTableFilter: undefined,
    sortBy: ticketsSortByOptions[4].value,
    sortAscending: false,
    activeData: {
        record: {},
        loading: false,
        selectedId: null,
        errorMessages: [],
        invoices: {
            loading: false,
            errorMessages: [],
            data: [],
            pageData: {
                pageSize: DETAILS_TAB.INVOICE_LIST.pageSize,
                currentPage: 0,
                hasNextPage: false,
            },
            filters: {
                InvoiceNumber: '',
                Customer: '',
                CustomerCode: '',
                CreatedDateMin: '',
                CreatedDateMax: '',
                DueDateMin: '',
                DueDateMax: '',
                AmountType: '',
                AmountValue: '',
                AmountOperator: '',
            },
            sortBy: commonInvoiceListInvoicesSortOptions[0].value, //tasksSortByOptions[0].value,
            sortAscending: true,
            invoiceState: get(invoicesStateFilterOptions, `${1}.value`),
        },
        tasks: {
            loading: false,
            errorMessages: [],
            data: [],
            pageData: {
                pageSize: CUSTOMER_ASSISTANCE_PAGE.dataTasks.pageSize,
                currentPage: 0,
                hasNextPage: false,
            },
            filters: {},
            sortBy: '', //tasksSortByOptions[0].value,
            sortAscending: true,
        },
        conversation: {
            loading: false,
            errorMessages: [],
            data: [],
            pageData: {
                pageSize: DETAILS_TAB.CONVERSATION_TIMELINE.pageSize,
                currentPage: 0,
                hasNextPage: false,
            },
            filters: {},
            conversationTableFilter: get(
                conversationTableFilterOptions,
                '0.value'
            ),
            sortBy: 'Created Date Time',
            sortAscending: false,
        },
        discussion: {
            loading: false,
            errorMessages: [],
            data: [],
            pageData: {
                pageSize: DETAILS_TAB.CONVERSATION_TIMELINE.pageSize,
                currentPage: 0,
                hasNextPage: false,
            }
        }
    },
};

const ticketSlice = createSlice({
    name: actionPrefix,
    initialState,
    reducers: {
        getTicketsRequestAction: (
            state,
            _action: PayloadAction<GetTicketsRequestPayload>
        ) => {
            state.loading = true;
        },
        getTicketsSuccessAction: (
            state,
            action: PayloadAction<{
                data: Ticket[];
                pageData: PageData;
                mergeData?: boolean;
            }>
        ) => {
            if (action.payload.mergeData === true) {
                state.data = values(
                    merge(
                        keyBy(state.data, 'Id'),
                        keyBy(action.payload.data, 'Id')
                    )
                );
            } else if (action.payload.pageData.currentPage === 0) {
                state.data = action.payload.data;
            } else {
                state.data = uniqWith(
                    [...state.data, ...action.payload.data],
                    isEqual
                );
            }

            state.loading = false;
            state.pageData = action.payload.pageData;
            state.errorMessages = initialState.errorMessages;
        },
        getTicketsErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.loading = false;
            state.data = initialState.data;
            state.errorMessages = action.payload;
        },
        updateTicketsFiltersAction: (
            state,
            action: PayloadAction<GetTicketsRequestPayload['filters']>
        ) => {
            state.filters = action.payload;
        },
        updateTicketsSortByAndStatusAction: (
            state,
            action: PayloadAction<{
                sortBy: GetTicketsRequestPayload['sortBy'];
                sortAscending: boolean;
                ticketStatus: number;
            }>
        ) => {
            state.sortBy = action.payload.sortBy;
            state.sortAscending = action.payload.sortAscending;
            state.ticketStatus = action.payload.ticketStatus;
        },
        updateTicketsTableFilterAction: (
            state,
            action: PayloadAction<string>
        ) => {
            state.ticketTableFilter = action.payload;
        },
        clearTicketsStateAllTableFiltersAction: (state) => {
            state.filters = initialState.filters;
            state.sortBy = initialState.sortBy;
            state.sortAscending = initialState.sortAscending;
            state.ticketStatus = initialState.ticketStatus;
            state.ticketTableFilter = initialState.ticketTableFilter;
        },
        clearTicketsStateDataAction: (_state) => {
            return initialState;
        },
        setSelectedTicketIdRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    ticketId: string | null;
                    callback: () => void;
                }>
            ) => {
                state.activeData.loading = true;
            },
            prepare: (
                ticketId: string | null,
                callback: () => void
            ) => ({
                payload: {
                    ticketId,
                    callback
                }
            })
        },
        setSelectedTicketIdSuccessAction: (
            state,
            action: PayloadAction<string | null>
        ) => {
            state.activeData.selectedId = action.payload;
            state.activeData.loading = false;
        },
        getTicketDataRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    ticketId: string;
                    isUsingCloudImportType: boolean;
                }>
            ) => {
                state.activeData.loading = true;
            },
            prepare: (
                ticketId: string,
                isUsingCloudImportType: boolean
            ) => ({
                payload: {
                    ticketId,
                    isUsingCloudImportType
                }
            })
        },
        getTicketDataSuccessAction: (
            state,
            action: PayloadAction<{ record: {} }>
        ) => {
            state.activeData.record = action.payload.record;
            state.activeData.loading = false;
            state.activeData.errorMessages = initialState.activeData.errorMessages;
        },
        getTicketDataErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.activeData.record = initialState.activeData.record;
            state.activeData.loading = false;
            state.activeData.errorMessages = action.payload;
        },
        clearTicketDataAction: (state) => {
            state.activeData.record = initialState.activeData.record;
            state.activeData.loading = initialState.activeData.loading;
            state.activeData.selectedId = initialState.activeData.selectedId;
            state.activeData.errorMessages = initialState.activeData.errorMessages;
            state.activeData.invoices = initialState.activeData.invoices;
            state.activeData.tasks = initialState.activeData.tasks;
        },
        getTicketDataInvoicesRequestAction: (
            state,
            _action: PayloadAction<GetTicketDataInvoicesRequestPayload>
        ) => {
            state.activeData.invoices.loading = true;
        },
        getTicketDataInvoicesSuccessAction: (
            state,
            action: PayloadAction<{
                data: Invoice[];
                pageData: PageData;
            }>
        ) => {
            if (action.payload.pageData.currentPage === 0) {
                state.activeData.invoices.data = action.payload.data;
            } else {
                state.activeData.invoices.data = uniqWith(
                    [...state.activeData.invoices.data, ...action.payload.data],
                    isEqual
                );
            }

            state.activeData.invoices.loading = false;
            state.activeData.invoices.pageData = action.payload.pageData;
            state.activeData.invoices.errorMessages = initialState.activeData.invoices.errorMessages;
        },
        getTicketDataInvoicesErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.activeData.invoices.loading = false;
            state.activeData.invoices.data = initialState.activeData.invoices.data;
            state.activeData.invoices.errorMessages = action.payload;
        },
        updateTicketDataInvoicesFiltersAction: (
            state,
            action: PayloadAction<GetTicketDataInvoicesRequestPayload['filters']>
        ) => {
            state.activeData.invoices.filters = action.payload;
        },
        updateTicketDataInvoicesSortByAndStateAction: (
            state,
            action: PayloadAction<{
                sortBy: GetTicketDataInvoicesRequestPayload['sortBy'];
                sortAscending: boolean;
                invoiceState: string;
            }>
        ) => {
            state.activeData.invoices.sortBy = action.payload.sortBy;
            state.activeData.invoices.sortAscending = action.payload.sortAscending;
            state.activeData.invoices.invoiceState = action.payload.invoiceState;
        },
        getTicketDataTasksRequestAction: (
            state,
            _action: PayloadAction<GetTicketDataTasksRequestPayload>
        ) => {
            state.activeData.tasks.loading = true;
        },
        getTicketDataTasksSuccessAction: (
            state,
            action: PayloadAction<{
                data: Task[];
                pageData: PageData;
            }>
        ) => {
            if (action.payload.pageData.currentPage === 0) {
                state.activeData.tasks.data = action.payload.data;
            } else {
                state.activeData.tasks.data = uniqWith(
                    [...state.activeData.tasks.data, ...action.payload.data],
                    isEqual
                );
            }

            state.activeData.tasks.loading = false;
            state.activeData.tasks.pageData = action.payload.pageData;
            state.activeData.tasks.errorMessages = initialState.activeData.tasks.errorMessages;
        },
        getTicketDataTasksErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.activeData.tasks.loading = false;
            state.activeData.tasks.data = initialState.activeData.tasks.data;
            state.activeData.tasks.errorMessages = action.payload;
        },
        updateTicketDataTasksFiltersAction: (
            state,
            action: PayloadAction<GetTicketDataTasksRequestPayload['filters']>
        ) => {
            state.activeData.tasks.filters = action.payload;
        },
        //updateTicketDataTasksSortByAction: (
        //    state,
        //    action: PayloadAction<{
        //         sortBy: GetTicketDataTasksRequestPayload['sortBy'];
        //         sortAscending: boolean;
        //    }>
        //) => {
        //    state.activeData.tasks.sortBy = action.payload.sortBy;
        //    state.activeData.tasks.sortAscending = action.payload.sortAscending;
        //},
        getTicketConversationRequestAction: (
            state,
            _action: PayloadAction<GetConversationRequestPayload>
        ) => {
            state.activeData.conversation.loading = true;
        },
        getTicketConversationSuccessAction: (
            state,
            action: PayloadAction<{
                data: Conversation[];
                pageData: PageData;
            }>
        ) => {
            if (action.payload.pageData.currentPage === 0) {
                state.activeData.conversation.data = action.payload.data;
            } else {
                state.activeData.conversation.data = uniqWith(
                    [
                        ...state.activeData.conversation.data,
                        ...action.payload.data
                    ],
                    isEqual
                );
            }

            state.activeData.conversation.loading = false;
            state.activeData.conversation.pageData = action.payload.pageData;
            state.activeData.conversation.errorMessages = initialState.activeData.conversation.errorMessages;
        },
        getTicketConversationErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.activeData.conversation.loading = false;
            state.activeData.conversation.data = initialState.activeData.conversation.data;
            state.activeData.conversation.errorMessages = action.payload;
        },
        getTicketDiscussionRequestAction: (
            state,
            _action: PayloadAction<GetTicketDiscussionRequestPayload>
        ) => {
            state.activeData.discussion.loading = true;
        },
        getTicketDiscussionSuccessAction: (
            state,
            action: PayloadAction<{
                data: ConversationLine[];
                pageData: PageData;
            }>
        ) => {
            if (action.payload.pageData.currentPage === 0) {
                state.activeData.discussion.data = action.payload.data;
            } else {
                state.activeData.discussion.data = uniqWith(
                    [
                        ...state.activeData.discussion.data,
                        ...action.payload.data
                    ],
                    isEqual
                );
            }

            state.activeData.discussion.loading = false;
            state.activeData.discussion.pageData = action.payload.pageData;
            state.activeData.discussion.errorMessages = initialState.activeData.discussion.errorMessages;
        },
        getTicketDiscussionErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.activeData.discussion.loading = false;
            state.activeData.discussion.data = initialState.activeData.discussion.data;
            state.activeData.discussion.errorMessages = action.payload;
        },
        clearTicketDiscussionAction: (state) => {
            state.activeData.discussion = initialState.activeData.discussion;
        },
        updateTicketConversationFiltersAction: (
            state,
            action: PayloadAction<GetConversationRequestPayload['filters']>
        ) => {
            state.activeData.conversation.filters = action.payload;
        },
        updateTicketConversationTableFilterAction: (
            state,
            action: PayloadAction<string>
        ) => {
            state.activeData.conversation.conversationTableFilter = action.payload;
        },
        updateTicketConversationSortByAction: (
            state,
            action: PayloadAction<{
                sortBy: GetConversationRequestPayload['sortBy'];
                sortAscending: boolean;
            }>
        ) => {
            state.activeData.conversation.sortBy = action.payload.sortBy;
            state.activeData.conversation.sortAscending = action.payload.sortAscending;
        },
        ticketAssignUserRequestAction: (
            state,
            _action: PayloadAction<TicketAssignUserRequestPayload>
        ) => {
            state.activeData.loading = true;
        },
    }
});

const { actions, reducer } = ticketSlice;

// Actions
export const {
    getTicketsRequestAction,
    getTicketsSuccessAction,
    getTicketsErrorAction,
    updateTicketsFiltersAction,
    updateTicketsSortByAndStatusAction,
    updateTicketsTableFilterAction,
    clearTicketsStateAllTableFiltersAction,
    clearTicketsStateDataAction,
    setSelectedTicketIdRequestAction,
    setSelectedTicketIdSuccessAction,
    getTicketDataRequestAction,
    getTicketDataSuccessAction,
    getTicketDataErrorAction,
    clearTicketDataAction,
    getTicketDataInvoicesRequestAction,
    getTicketDataInvoicesSuccessAction,
    getTicketDataInvoicesErrorAction,
    updateTicketDataInvoicesFiltersAction,
    updateTicketDataInvoicesSortByAndStateAction,
    getTicketDataTasksRequestAction,
    getTicketDataTasksSuccessAction,
    getTicketDataTasksErrorAction,
    updateTicketDataTasksFiltersAction,
    getTicketConversationRequestAction,
    getTicketConversationSuccessAction,
    getTicketConversationErrorAction,
    getTicketDiscussionRequestAction,
    getTicketDiscussionSuccessAction,
    getTicketDiscussionErrorAction,
    clearTicketDiscussionAction,
    updateTicketConversationFiltersAction,
    updateTicketConversationTableFilterAction,
    updateTicketConversationSortByAction,
    ticketAssignUserRequestAction
} = actions;

export const checkTicketsReadyStateAction = createAction(
    `${actionPrefix}/checkTicketsReadyStateAction`,
    function prepare(
        payload: GetTicketsRequestPayload
    ) {
        return {
            payload
        };
    }
);

export const getResolveTicketOptionsRequestAction = createAction(
    `${actionPrefix}/getResolveTicketOptionsRequestAction`,
    function prepare(
        workflowId: string,
        stateName: string,
        companyId: string,
        callback?: (response: any) => void
    ) {
        return {
            payload: {
                workflowId,
                stateName,
                companyId,
                callback
            }
        };
    }
);

export const resolveQueryRequestAction = createAction(
    `${actionPrefix}/resolveQueryRequestAction`,
    function prepare(
        payload: {}
    ) {
        return {
            payload
        };
    }
);

export const resolveOrganisationQueryRequestAction = createAction(
    `${actionPrefix}/resolveOrganisationQueryRequestAction`,
    function prepare(
        payload: {}
    ) {
        return {
            payload
        };
    }
);

export const ticketAddCommentRequestAction = createAction(
    `${actionPrefix}/ticketAddCommentRequestAction`,
    function prepare(
        payload: TicketAddCommentRequestPayload
    ) {
        return {
            payload
        };
    }
);

export const ticketResourceReplyRequestAction = createAction(
    `${actionPrefix}/ticketResourceReplyRequestAction`,
    function prepare(
        payload: TicketResourceReplyRequestPayload
    ) {
        return {
            payload
        };
    }
);

export const getTicketInfoForChatValidation = createAction(
    `${actionPrefix}/getTicketInfoForChatValidation`,
    function prepare(
        payload: GetTicketInfoForChatValidationPayload
    ) {
        return {
            payload
        };
    }
);

export const getTicketCustomFieldsForCompanyRequestAction = createAction(
    `${actionPrefix}/getTicketCustomFieldsForCompanyRequestAction`,
    function prepare(
        companyId: string,
        callback?: (response: DynamicObject) => void
    ) {
        return {
            payload: { companyId, callback }
        };
    }
);

export const getTicketCustomFieldsForOrganisationRequestAction = createAction(
    `${actionPrefix}/getTicketCustomFieldsForOrganisationRequestAction`,
    function prepare(
        CompanyIds: string[],
        FieldName?: string[],
        callback?: (response: DynamicObject) => void
    ) {
        return {
            payload: {
                CompanyIds,
                FieldName,
                callback
            }
        };
    }
);

export const ActionTicketRequestAction = createAction(
    `${actionPrefix}/ActionTicketRequestAction`,
    function prepare(
        payload: {}
    ) {
        return {
            payload
        };
    }
);

export const getActionResolveTicketOptionsRequestAction = createAction(
    `${actionPrefix}/getActionResolveTicketOptionsRequestAction`,
    function prepare(
        filters: any,
        pageSize: number,
        currentPage: number,
        companyId: string,
        callback?: (response: any) => void
    ) {
        return {
            payload: {
                filters,
                pageSize,
                currentPage,
                companyId,
                callback
            }
        };
    }
);

// Selectors
export const getTicketsState = (state: RootState) => state.tickets;
export const getTicketsPageData = createSelector(
    getTicketsState,
    (tickets) => tickets.pageData
);
export const getTicketsData = createSelector(
    getTicketsState,
    (tickets) => tickets.data
);
export const getTicketData = createSelector(
    getTicketsState,
    (tickets) => tickets.activeData
);
export const getTicketActiveDataRecord = createSelector(
    getTicketData,
    (activeData) => activeData.record
);
export const getSelectedTicketId = createSelector(
    getTicketData,
    (activeData) => activeData.selectedId
);
export const getTicketActiveDataDiscussion = createSelector(
    getTicketData,
    (activeData) => activeData.discussion
);
export const getTicketActiveDataInvoices = createSelector(
    getTicketData,
    (activeData) => activeData.invoices
);

// Reducer
export default reducer;